import { ChangeDetectionStrategy, Component, Injectable, signal, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter } from '@angular/material/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import moment, { Moment } from 'moment';

import { DateFormatEnum } from '../../models/date-format.enum';
import { DateInputEnum } from '../../models/date-input.enum';
import { valueToMoment } from '../../utils/dates-utils';
import { DateCellEditorInlineComponent } from './date-cell-editor-inline/date-cell-editor-inline.component';
import { DateCellEditorPopupComponent } from './date-cell-editor-popup/date-cell-editor-popup.component';

export interface IDateCellEditorParams {
    inlineEditor?: boolean; // true di default;
    required?: boolean;
    inputType: DateInputEnum | null;
    inputFormat: DateFormatEnum | null; //NB non è usato!!!
    dateInputFormat?: string;
    timeInputFormat?: string;
    dateTimeInputFormat?: string;
    dateSeparator?: string;
    timeSeparator?: string;
}

const requiredMoment = (c: AbstractControl<Moment | null>) =>
    c.value && c.value.isValid() ? null : { required: true };
@Injectable()
export class SeUiDateAdapter extends MomentDateAdapter {
    getFirstDayOfWeek = () => 1;
}
/**
 * Editor Custom AgGrid for Date DateTime Time Input.
 * Params:
 * - inputType: (Optional) it allows to specify 'DATE_TIME', 'DATE' or 'TIME'. Default value DATE.
 * - inputFormat: (Optional) it allows to specify 'SHORT' 'LONG' format for DATE_TIME, DATE e TIME. Default value SHORT. ---> NB non è usato!!!
 * - required: (Optional) It allows to specify required validator
 * - inlineEditor (Default true) it allows to specify the preference between online or popup editor version.
 *  NOTE: when in the AgGrid is enabled the fullRow editing this input param is ignored and the editor will be inline
 */
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-date-cell-editor',
    templateUrl: './date-cell-editor.component.html',
    styleUrls: ['./date-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: DateAdapter, useClass: SeUiDateAdapter }],
    imports: [ReactiveFormsModule, DateCellEditorInlineComponent, DateCellEditorPopupComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateCellEditorComponent implements ICellEditorAngularComp {
    readonly editInline = signal<boolean>(false);
    readonly inputType = signal<DateInputEnum | null>(null);
    //readonly inputFormat = signal<DateFormatEnum | null>(null); ---> NB non è usato!!!
    readonly dateInputFormat = signal<string>('');
    readonly dateTimeInputFormat = signal<string>('');
    readonly timeInputFormat = signal<string>('');
    readonly dateSeparator = signal<string>('');
    readonly timeSeparator = signal<string>('');
    readonly cellWidth = signal<string>('');
    readonly cellReady = signal<boolean>(false);
    readonly formControl = new FormControl<Moment | null>(null);
    /** originaValue è un fallback al valore precedente se non si inserisce una data valida e si chiude l'editing */
    private originalValue: Moment | null = null;

    agInit(params: ICellEditorParams & IDateCellEditorParams): void {
        const momentValue = valueToMoment(params.value);

        this.originalValue = momentValue;
        const inputType = params.inputType ?? DateInputEnum.DATE;
        let cellWidth = params.column.getActualWidth() + 'px';
        if (inputType === DateInputEnum.TIME) cellWidth = '75px';
        else if (inputType === DateInputEnum.DATE_TIME) cellWidth = '250px';
        this.inputType.set(inputType);
        this.cellWidth.set(cellWidth);
        this.editInline.set(params.inlineEditor !== false);
        this.dateInputFormat.set(params.dateInputFormat ?? 'DDMMYYYY');
        this.timeInputFormat.set(params.timeInputFormat ?? 'HH:mm');
        this.dateTimeInputFormat.set(params.dateTimeInputFormat ?? 'DDMMYYYY HH:mm');
        this.dateSeparator.set(params.dateSeparator ?? '/');
        this.timeSeparator.set(params.timeSeparator ?? ':');
        if (params.required) this.formControl.addValidators(requiredMoment);
        this.formControl.setValue(momentValue, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    getValue(): string | null {
        const raw = this.formControl.getRawValue();

        // se vuoto: per editor non-required può diventare null
        if (!raw) return null;

        // se invalido: fallback al valore precedente (se presente)
        if (!raw.isValid()) return this.originalValue?.toISOString() ?? null;

        const type = this.inputType() ?? DateInputEnum.DATE;

        // base = original se esiste, altrimenti un default coerente
        const base =
            this.originalValue?.clone() ??
            (type === DateInputEnum.TIME ? moment.utc('1970-01-01T00:00:00.000Z') : moment());

        // lavora in UTC per comporre l'istante in modo coerente (senza reinterpretazioni)
        const rawZ = raw.clone().utc();
        const outZ = base.clone().utc();

        switch (type) {
            case DateInputEnum.DATE_TIME: {
                outZ.set({
                    year: rawZ.year(),
                    month: rawZ.month(),
                    date: rawZ.date(),
                    hour: rawZ.hour(),
                    minute: rawZ.minute(),
                    second: rawZ.second(),
                    millisecond: rawZ.millisecond(),
                });
                return outZ.toISOString();
            }

            case DateInputEnum.DATE: {
                outZ.set({
                    year: rawZ.year(),
                    month: rawZ.month(),
                    date: rawZ.date(),
                });

                // se non c'era un originale, azzera il tempo
                if (!this.originalValue) {
                    outZ.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                }

                return outZ.toISOString();
            }

            case DateInputEnum.TIME: {
                outZ.set({
                    hour: rawZ.hour(),
                    minute: rawZ.minute(),
                    second: rawZ.second(),
                    millisecond: rawZ.millisecond(),
                });
                return outZ.toISOString();
            }

            default:
                return raw.toISOString();
        }
    }

    isPopup(): boolean {
        // lasciamo sempre TRUE anche quando instanziamo il componente inline per le DATE
        // altrimenti non riusciamo a selezionare i dati dal picker
        return true;
    }

    afterGuiAttached(): void {
        this.cellReady.set(true);
    }
}
